import moment from 'moment';
import type { Dispatch } from 'redux';
import request from '@utils/request';
import {
  ConfigurationGroupOptionType,
  ConfigurationGroupType,
  SpaPackageType,
  TimeSlotType,
} from 'shared-types/SpaPackage';
import { EntityName } from '../entities-map';
import { NormalizedStore } from '../entity.types';
import {
  fetchEntitySingle,
  fetchPaginatedEntity,
  fetchPartialEntity,
  fetchEntityPerDayRequest,
  fetchEntityPerDaySuccess,
  fetchEntityPerDayFailure,
} from '../actions/base.actions';
import { fetchSplitAvailabilities } from '../actions/other.actions';
import { FetchSpaPackageConfigGroupsArgs, FetchSpaPackageTimeslotsArgs } from './types';
import { entitySelectors } from '../selectors';

export const fetchSpaPackages = (
  propertyId: number,
  language: string,
  date: string | null,
  category: string | string[] | undefined,
  perPage = 0
) =>
  fetchPaginatedEntity(EntityName.spaPackages, {
    url: `${propertyId}/spa-packages/${language}`,
    params: {
      category,
      perPage,
      date,
    },
    isOptional: false,
  });

export const fetchSpaPackagesAll = (propertyId, language) =>
  fetchPaginatedEntity(EntityName.spaPackagesAll, {
    url: `${propertyId}/spa-packages/${language}`,
    isOptional: false,
  });

export const fetchSpaDayAvailabilities = fetchSplitAvailabilities(
  'spaPackages',
  ({ propertyId, language }) => `${propertyId}/spa-packages/${language}`,
  true
);

const ConfigurationGroupsItemName = 'configurationGroups';

/**
 * @description Clear isBookable and timeslots from given configuration groups
 * @param givenItems
 * @returns Cleared configurationGroups
 */
const clearConfigGroups = (givenItems: ConfigurationGroupType[]): ConfigurationGroupType[] =>
  givenItems.map((item) => {
    const { options, isBookable: _, ...rest } = item;
    const cleanOptions = options?.map(({ timeslots: _, ...restOption }) => restOption);
    return { ...rest, options: cleanOptions };
  });

export const fetchSpaPackage = (propertyId, spaDayId, language) =>
  fetchEntitySingle(EntityName.spaPackages, {
    url: `${propertyId}/spa-packages/${spaDayId}/${language}`,
    isOptional: false,
  });

/**
 * @description
 * Clears the configurationGroup partial entity data from the entity
 * and puts in the received time slots on the entity
 * @param data SpaPackage object
 * @param _ unused
 * @param receivedItems new timeSlots collection
 * @returns decorated SpaPackage object
 */
export const timeslotsCustomizer = (
  data: Omit<SpaPackageType, 'timeSlots'>,
  _: TimeSlotType[] = [],
  receivedItems: TimeSlotType[]
): SpaPackageType => {
  // Clear configuration groups
  const { configurationGroups, ...rest } = data;
  const cleanConfigGroups = clearConfigGroups(configurationGroups);

  // Put in timeSlots and clean configuration groups
  return {
    ...rest,
    timeSlots: receivedItems,
    [ConfigurationGroupsItemName]: cleanConfigGroups,
  };
};

export const fetchSpaPackageTimeslot =
  ({ id, quantity, day }) =>
  async (dispatch, getState) => {
    const entityName = 'spaPackages';
    dispatch(fetchEntityPerDayRequest(entityName, day));
    const state = getState();
    const propertyId = entitySelectors.getProperty(state).id;
    const url = `${propertyId}/spa-packages/${id}/${day}/timeslots`;
    return request
      .get(url, { params: { quantity } })
      .then(({ data }) => {
        const entity = entitySelectors.getSpaPackage(getState(), id);
        dispatch(
          fetchEntityPerDaySuccess(
            entityName,
            {
              ...entity,
              timeslots: { ...entity.timeslots, [day]: data },
            },
            day,
            true
          )
        );
      })
      .catch(() => {
        dispatch(fetchEntityPerDayFailure(entityName, day));
      });
  };

export const fetchSpaPackageTimeslots =
  ({ id, startDate, quantity }: FetchSpaPackageTimeslotsArgs) =>
  async (dispatch, getState) => {
    await Promise.all(
      new Array(7)
        .fill('')
        .map((__, index) => moment(startDate).add(index, 'days').format('YYYY-MM-DD'))
        .map((day) => fetchSpaPackageTimeslot({ id, quantity, day })(dispatch, getState))
    );
  };

/**
 * @description
 * Removes the timeslots in the given configuration groups collection, ie. origItems,
 * and picks the timeslots in the receivedItem and puts them in corresponding configOption/option
 * of origItems. Shortly, it decorates the SpaPackage with timeslots.
 * @param SpaPackageType SpaPackage object
 * @param origItems existing configuration groups
 * @param receivedItems new configuration groups (with timeslots)
 * @returns decorated SpaPackage object
 */
export const configGroupFetchCustomizer = (
  data: Omit<SpaPackageType, 'configurationGroups'>,
  origItems: ConfigurationGroupType[],
  receivedItems: ConfigurationGroupType[]
): SpaPackageType => {
  // Clear isBookable and timeslots from all config groups
  const configGroups = clearConfigGroups(origItems);

  // Put isBookable and timeslots in configGroup/option
  receivedItems.forEach(({ id, isBookable, options }) => {
    const configGroup = configGroups?.find(
      (cleanItem) => cleanItem.id === id
    ) as ConfigurationGroupType;
    if (configGroup) {
      configGroup.isBookable = isBookable;
    }
    options?.forEach(({ id: optionId, timeslots: receivedTimeslots }) => {
      const targetOption = configGroup?.options.find((cleanOption) => cleanOption.id === optionId);
      if (targetOption && receivedTimeslots) {
        (targetOption as ConfigurationGroupOptionType).timeslots = [...receivedTimeslots];
      }
    });
  });

  const configurationGroupValidator: { id: number; isValid: boolean }[] = [];

  receivedItems.forEach(({ id }) => {
    configurationGroupValidator.push({ id, isValid: false });
  });

  return { ...data, [ConfigurationGroupsItemName]: configGroups };
};

export const fetchSpaPackageConfigGroups = ({
  propertyId,
  spaPackageId,
  arrivalDate,
  quantity,
  startTime,
  endTime,
}: FetchSpaPackageConfigGroupsArgs): ((
  dispatch: Dispatch,
  getState: () => NormalizedStore
) => Promise<void>) => {
  const date = moment(arrivalDate).format('YYYY-MM-DD');
  return fetchPartialEntity<SpaPackageType, ConfigurationGroupType[]>(
    EntityName.spaPackages,
    spaPackageId,
    ConfigurationGroupsItemName,
    {
      url: `${propertyId}/spa-packages/${spaPackageId}/configuration-groups/${date}/timeslots`,
      params: {
        quantity,
        'start-time': startTime,
        'end-time': endTime,
      },
      isOptional: true,
    },
    configGroupFetchCustomizer
  );
};
